import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Search, Package, Download } from "lucide-react";

export default function OrderLookup() {
  const [email, setEmail] = useState("");
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  async function search(e) {
    e.preventDefault();
    setLoading(true); setErr(""); setOrders(null);
    try {
      const { data } = await api.get("/orders/lookup/by-email", { params: { email } });
      setOrders(data);
    } catch (er) {
      setErr(formatApiError(er.response?.data?.detail) || "Couldn't find orders.");
    } finally { setLoading(false); }
  }

  return (
    <div data-testid="order-lookup-page" className="max-w-3xl mx-auto px-4 py-20">
      <div className="text-center">
        <div className="uppercase text-xs tracking-[0.4em] text-[#5C5547]">Self-service</div>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl gold-heading">Find your order.</h1>
        <p className="mt-3 text-[#5C5547]">Enter the email you used at checkout to see your order status & download links.</p>
      </div>

      <form onSubmit={search} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5547]" />
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            data-testid="lookup-email"
            className="w-full pl-11 pr-4 py-3.5 rounded-full bg-[#F5F2EA] border-2 border-[var(--accent)]/40 focus:border-[var(--accent)] focus:outline-none text-sm" />
        </div>
        <button type="submit" disabled={loading} data-testid="lookup-submit"
          className="px-7 py-3.5 rounded-full bg-[#2C2924] text-[#FDFBF7] hover:bg-[#1A1815] text-sm font-medium disabled:opacity-60">
          {loading ? "Searching…" : "Find orders"}
        </button>
      </form>
      {err && <div className="mt-4 text-center text-sm text-red-600">{err}</div>}

      {orders !== null && (
        <div className="mt-10 space-y-4">
          {orders.length === 0 ? (
            <div className="text-center py-10 text-[#5C5547]" data-testid="lookup-empty">No orders found for that email.</div>
          ) : orders.map((o) => (
            <div key={o.id} data-testid={`lookup-order-${o.id}`}
              className="p-5 rounded-2xl bg-[#FDFBF7] flex items-center justify-between gap-4 flex-wrap"
              style={{ border: "3px solid var(--accent)" }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#F5F2EA] flex items-center justify-center">
                  <Package className="w-5 h-5 text-[var(--accent)]" />
                </div>
                <div>
                  <div className="font-medium">{o.product_title}</div>
                  <div className="text-xs text-[#5C5547]">via {o.payment_method} · {o.id.slice(0, 8)}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`uppercase tracking-[0.15em] text-xs ${o.status === "paid" ? "text-[var(--accent)]" : o.status === "fulfilled" ? "text-green-700" : "text-[#5C5547]"}`}>{o.status}</span>
                {o.status === "paid" && o.download_token && (
                  <a href={`/api/orders/${o.id}/download?token=${o.download_token}`} data-testid={`lookup-download-${o.id}`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-[var(--accent)] text-black text-xs font-semibold">
                    <Download className="w-3.5 h-3.5" /> Download
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
